import { useCallback, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import {
  FertilizationWithEmbryos,
  TOA_GetFertilizationsForFreezing,
} from '@/rtk-query.gql';

import { SelectionService } from '../../utils/selectionService';

export function useValidation({
  fertEmbryosData,
  fertilizationData,
  fertilizationSiteId,
}: {
  fertilizationData: TOA_GetFertilizationsForFreezing[] | undefined;
  fertEmbryosData: FertilizationWithEmbryos[];
  fertilizationSiteId: string | null;
}) {
  const dispatch = useDispatch();
  const [showMissingLocationsDialog, setShowMissingLocationsDialog] =
    useState(false);
  const [missingLocationsMessages, setMissingLocationsMessages] = useState<
    string[]
  >([]);
  const [showNoGraderAndFreezeTech, setShowNoGraderAndFreezeTech] =
    useState(false);
  const [allFertMathCorrect, setAllFertMathCorrect] = useState(true);

  const validation = useMemo(() => {
    const sorted = [...(fertilizationData || [])]
      .filter((f) => f.validationDate)
      .sort(
        (a, b) =>
          new Date(b.validationDate as string).getTime() -
          new Date(a.validationDate as string).getTime(),
      );

    return {
      validatorId: sorted[0]?.validatorId as string,
      validationDate: sorted[0]?.validationDate
        ? new Date(sorted[0].validationDate as string)
        : undefined,
    };
  }, [fertilizationData]);

  const findMissingStorageLocations = useCallback(() => {
    const selection = SelectionService.getSelection();
    const { liveEmbryoIds, fertilizationIds } = selection;
    const missing: Array<{
      fertId: string;
      plateNumber: number;
      donor: string;
      sire: string;
    }> = [];
    const messages: string[] = [];

    fertilizationIds.forEach((fertId) => {
      const fertData = fertilizationData?.find((f) => f.fertId === fertId);
      const fertEmbryos = fertEmbryosData?.find((f) => f.fertId === fertId);
      if (!fertData || !fertEmbryos) return;
      const plateNumber = fertData.plateNumber as number;
      const donor = fertData.donorName as string;
      const sire = fertData.sireName as string;

      fertEmbryos.liveEmbryos?.forEach((item) => {
        if (!liveEmbryoIds.includes(item.id as string)) return;
        if (
          !item.storageLocationId &&
          !missing.some((m) => m.fertId === fertId)
        ) {
          missing.push({
            fertId: fertId as string,
            plateNumber,
            donor,
            sire,
          });
          messages.push(`Plate ${plateNumber} - ${donor} x ${sire}`);
        }
        if (!item.grader7Name || !item.freezeTechName) {
          messages.push(
            `Plate ${plateNumber} - ${donor} x ${sire}: Missing ${!item.grader7Name ? 'grader' : 'freeze tech'}`,
          );
          setShowNoGraderAndFreezeTech(true);
        }
      });
    });
    if (messages.length > 0) {
      setMissingLocationsMessages(messages);
      setShowMissingLocationsDialog(true);
    }
    return missing;
  }, [fertilizationData, fertEmbryosData]);

  const checkEmbryoConditions = useCallback(() => {
    const selection = SelectionService.getSelection();
    if (
      !selection.fertilizationIds ||
      selection.fertilizationIds.length === 0
    ) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'No fertilizations selected',
          sticky: false,
        }),
      );
      return { allEmbryosContainStorageLocation: false };
    }
    const selectedFertIds = selection.fertilizationIds || [];

    const filteredFertilizations =
      fertilizationData?.filter(
        (data) =>
          data.fertId != null &&
          selectedFertIds.includes(data.fertId.toString()),
      ) || [];

    if (filteredFertilizations.length === 0) {
      setAllFertMathCorrect(true);
      return { allEmbryosContainStorageLocation: true };
    }

    const missingLocations = findMissingStorageLocations();
    const allEmbryosContainStorageLocation = missingLocations.length === 0;
    const fertMath: string[] = [];

    filteredFertilizations.forEach((data) => {
      if (data?.noEmbryosProduced) {
        fertMath.push(`Green`);
      }

      if (data?.intEmbs === 0 && data?.embs === 0) {
        if (
          data?.heldAtTogToFreeze ===
            (data?.degenerates || 0) +
              (data?.discarded || 0) +
              (data?.discardedGrade2 || 0) +
              (data?.discardedGrade1 || 0) &&
          (data?.heldAtTogToFreeze || 0) > 0
        ) {
          fertMath.push(`Green`);
        } else {
          fertMath.push(`Orange`);
        }
      } else {
        if (
          fertilizationSiteId === '1' ||
          fertilizationSiteId === '2' ||
          fertilizationSiteId === '3'
        ) {
          if (
            (data?.embs || 0) +
              (data?.intEmbs || 0) +
              (data?.degenerates || 0) +
              (data?.discarded || 0) +
              (data?.discardedGrade2 || 0) +
              (data?.discardedGrade1 || 0) <
            (data?.heldAtTogToFreeze || 0)
          ) {
            fertMath.push(`Red`);
          } else if ((data?.intEmbs as number) > 0) {
            fertMath.push(`Green`);
          } else if (
            data?.intEmbs === 0 &&
            data?.embs === data?.heldAtTogToFreeze
          ) {
            fertMath.push(`Green`);
          } else if (
            data?.heldAtTogToFreeze ===
              (data?.degenerates || 0) +
                (data?.discarded || 0) +
                (data?.discardedGrade2 || 0) +
                (data?.discardedGrade1 || 0) &&
            (data?.heldAtTogToFreeze || 0) > 0
          ) {
            fertMath.push(`Green`);
          }
        } else {
          if (
            (data?.embs || 0) +
              (data?.intEmbs || 0) +
              (data?.degenerates || 0) +
              (data?.discarded || 0) +
              (data?.discardedGrade2 || 0) +
              (data?.discardedGrade1 || 0) !==
            data?.heldAtTogToFreeze
          ) {
            fertMath.push(`Red`);
          } else if ((data?.intEmbs as number) > 0) {
            fertMath.push(`Yellow`);
          } else if (
            (data?.intEmbs as number) === 0 &&
            (data?.embs as number) +
              (data?.degenerates as number) +
              (data?.discarded as number) +
              (data?.discardedGrade2 as number) +
              (data?.discardedGrade1 as number) ===
              data?.heldAtTogToFreeze
          ) {
            fertMath.push(`Green`);
          }
        }
      }
      return null;
    });
    if (fertMath.some((item) => item.includes('Red'))) {
      setShowMissingLocationsDialog(true);
      setAllFertMathCorrect(false);
    } else {
      setAllFertMathCorrect(true);
    }

    return {
      allEmbryosContainStorageLocation,
    };
  }, [
    fertilizationData,
    findMissingStorageLocations,
    dispatch,
    fertilizationSiteId,
  ]);

  const checkEmbryoQCFields = useCallback(() => {
    const selection = SelectionService.getSelection();
    const selectedFertIds = selection.fertilizationIds || [];
    const filteredFertilizations =
      fertilizationData?.filter(
        (data) =>
          data.fertId != null &&
          selectedFertIds.includes(data.fertId.toString()),
      ) || [];

    if (
      !selection.fertilizationIds ||
      selection.fertilizationIds.length === 0
    ) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'No fertilizations selected',
          sticky: false,
        }),
      );
      return [];
    }

    const missingFieldsByEmbryo: Array<{
      embryoName: string;
      missingFields: string[];
    }> = [];
    filteredFertilizations.forEach((fert) => {
      const fertEmbryos = fertEmbryosData?.find(
        (f) => f.fertId === fert.fertId,
      );
      if (!fertEmbryos) return;
      const allEmbryos = [...(fertEmbryos.liveEmbryos || [])];
      allEmbryos.forEach((emb) => {
        const freezeMethod =
          emb.freezeMethodName?.toLowerCase() ||
          fert.freezeMethod?.toLowerCase() ||
          '';
        const isExport = fert.export?.toString().toLowerCase() === 'yes';
        const missingFields: string[] = [];
        // 1.5M EG w/ sucrose
        if (
          freezeMethod.includes('1.5m eg') &&
          freezeMethod.includes('suc') &&
          !freezeMethod.includes('vit')
        ) {
          if (!emb.qcEg10) missingFields.push('EG Lot Number');
          if (!emb.qcEg10Date) missingFields.push('EG Expiration Date');
          if (!emb.qcehmDate) missingFields.push('EHM Expiration Date');
          if (!emb.qcSucroseDate) missingFields.push('Sucrose Expiration Date');
          if (!emb.qcStrawLotNumber) missingFields.push('Straw Lot #');
          if (!emb.qc12WellPlates?.trim()) {
            missingFields.push('12 Well Lot #');
          }
        }
        // Vitrification
        else if (freezeMethod.includes('vit')) {
          if (!emb.qcehmDate) missingFields.push('EHM Expiration Date');
          if (!emb.equilibrationSolutionDate)
            missingFields.push('ES Expiration Date');
          if (!emb.vitrificationSolutionDate)
            missingFields.push('VS Expiration Date');
          if (!emb.qcVitStrawLotNumber) missingFields.push('Vit Straw Lot #');
        }
        // Export = YES
        if (isExport) {
          if (!emb.qcEg10) missingFields.push('EG Lot Number');
          if (!emb.qcEg10Date) missingFields.push('EG Expiration Date');
          if (!emb.qcehmDate) missingFields.push('EHM Expiration Date');
          if (!emb.qcSucroseDate) missingFields.push('Sucrose Expiration Date');
          if (!emb.qcSterileStrawLotNumber)
            missingFields.push('Sterile Straw Lot #');
          if (!freezeMethod.includes('vit') && !emb.qc12WellPlates?.trim()) {
            missingFields.push('12 Well Lot #');
          }
        }
        if (missingFields.length > 0) {
          missingFieldsByEmbryo.push({
            embryoName: emb.name || emb.id || 'Unknown',
            missingFields,
          });
        }
      });
    });
    return missingFieldsByEmbryo;
  }, [fertilizationData, dispatch, fertEmbryosData]);

  return {
    showMissingLocationsDialog,
    setShowMissingLocationsDialog,
    missingLocationsMessages,
    showNoGraderAndFreezeTech,
    setShowNoGraderAndFreezeTech,
    allFertMathCorrect,
    validation,
    checkEmbryoConditions,
    checkEmbryoQCFields,
  };
}
